import type { QueryClient } from '@tanstack/react-query';
import type { AnyRouter } from '@tanstack/react-router';
import { ApiError } from '../../lib/api';
import { resetSessionCache } from './session';

/**
 * Ce se întâmplă când serverul spune că sesiunea nu mai e bună.
 *
 * Înainte de #48 nu se întâmpla nimic: fiecare ecran își picta 401-ul ca pe orice altă
 * eroare — un text roșu în mijlocul cardului, într-o pagină care rămânea acolo, cu meniu
 * cu tot. Operatorul vedea „Sesiunea a expirat. Autentifică-te din nou." și n-avea de unde
 * să se autentifice.
 *
 * Se tratează **într-un singur loc**, pe cache-ul de query-uri și de mutații, nu pe fiecare
 * apel: un `if (error.status === 401)` copiat prin ecrane e exact genul de verificare care
 * se uită la al douăzecelea.
 */

type SessionExpiryWiring = {
  queryClient: QueryClient;
  router: AnyRouter;
};

/**
 * Legătura se face după construcție, nu prin import.
 *
 * `QueryCache` are nevoie de router ca să redirecționeze, iar routerul are nevoie de
 * `queryClient` în context (`beforeLoad` cere sesiunea prin el). Cu importuri directe ar
 * ieși un ciclu; cu o legare explicită în `main.tsx` iese o singură linie, vizibilă.
 */
let wiring: SessionExpiryWiring | null = null;

export function bindSessionExpiry(next: SessionExpiryWiring): void {
  wiring = next;
}

/** Doar pentru teste: desface legătura făcută de `main.tsx`. */
export function unbindSessionExpiry(): void {
  wiring = null;
}

/**
 * 401 înseamnă „nu ești (mai) autentificat", indiferent de motiv — token lipsă, expirat,
 * revocat, cont dezactivat. Serverul le dă deliberat pe toate la fel; vezi
 * `AuthenticateSessionUseCase`.
 *
 * 403 (`missing_permission`) **nu** intră aici: acolo sesiunea e vie, iar o delogare ar
 * ascunde faptul că operatorului îi lipsește un drept.
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Golește sesiunea locală și trimite operatorul la login, cu `?redirect=` către unde era.
 *
 * Idempotentă: dacă suntem deja pe `/login` nu face nimic. Fără paza asta, două query-uri
 * care pică simultan cu 401 ar produce două navigări, iar a doua ar seta `redirect` chiar
 * pe `/login` — adică o buclă.
 */
export function expireSession(): void {
  if (!wiring) {
    return;
  }

  const { queryClient, router } = wiring;
  const current = router.state.location.href;

  if (current.startsWith('/login')) {
    return;
  }

  resetSessionCache(queryClient, null);
  void router.navigate({ to: '/login', search: { redirect: current } });
}

/** Cârligul pus pe `QueryCache` și `MutationCache`. */
export function expireSessionOnUnauthorized(error: unknown): void {
  if (isUnauthorized(error)) {
    expireSession();
  }
}
